import { CheckSquare, Plus, Table2, Timer } from 'lucide-react';
import type { router as AppRouter } from '../../router';
import { registerCommands, type Command } from '../../shell/command-registry';
import { HM_NAV_ITEMS, canOpenModule } from '../../shell/nav-config';

/**
 * S9's palette entries (06-qol §1: every screen registers itself). Each names the
 * §L23 object it needs, so nobody is offered a destination they would be refused.
 *
 * The board is not here: the module bar already registers "Requisitions".
 */
export function registerRequisitionCommands(router: typeof AppRouter): () => void {
  const commands: Command[] = [
    {
      id: 'req.approvals',
      label: 'Approvals waiting on me',
      section: 'navigate',
      context: 'Requisitions · Waiting on me',
      permission: 'approval_action',
      icon: CheckSquare,
      // A hiring manager's approvals are on My actions (S10).
      canSee: (session) => !session.isHiringManagerSurface,
      keywords: ['approve', 'queue', 'pending', 'decide', 'requisitions'],
      run: () => void router.navigate({ to: '/requisitions', search: { view: 'mine' } }),
    },
    {
      id: 'req.new',
      label: 'Request a hire',
      section: 'action',
      context: 'Requisitions',
      permission: 'requisition',
      icon: Plus,
      canSee: (session) => !session.isHiringManagerSurface,
      keywords: ['requisition', 'new', 'hire', 'headcount', 'raise'],
      run: () => void router.navigate({ to: '/requisitions/new' }),
    },
    {
      id: 'req.chains',
      label: 'Approval chains',
      section: 'navigate',
      context: 'Admin · ADMIN-06',
      permission: 'admin_config',
      icon: Table2,
      keywords: ['chain', 'approvers', 'offer chain', 'sign-off'],
      run: () => void router.navigate({ to: '/admin/approval-chains' }),
    },
    {
      // Admin redesign 2026-11-02: Time limits is its own page.
      id: 'req.timeLimits',
      label: 'Time limits',
      section: 'navigate',
      context: 'Admin · how long an approval may wait',
      permission: 'admin_config',
      icon: Timer,
      keywords: ['sla', 'deadline', 'overdue', 'escalation', 'time limit'],
      run: () => void router.navigate({ to: '/admin/time-limits' }),
    },
  ];

  // S10: the three HM screens, by the module bar's own rule so the two agree.
  const hmScreens: Command[] = HM_NAV_ITEMS.map((item) => ({
    id: `nav.${item.id}`,
    label: item.label,
    section: 'navigate',
    context: `Screen · g then ${item.goKey ?? ''}`,
    canSee: (session, modules) => canOpenModule(item, session, modules),
    icon: item.icon,
    keywords: ['hiring manager', 'my', 'requisition'],
    run: () => void router.navigate({ to: item.to }),
  }));
  return registerCommands([...commands, ...hmScreens]);
}
